import { useLocation } from '@reach/router';
import { APP_URL } from 'constants/config';
import { Link, navigate } from 'gatsby';
import React from 'react';
import styled from 'components/styled/styled';
import { MENU_ITEMS } from 'constants/staticData.constants';
import { FlexContainer } from 'components/styled/layout.styled';
import { P } from 'components/styled/typography.styled';
import { Button } from 'components/styled/button.styled';
import MintaLogo from 'assets/images/minta_logo.png';
import ReactModal from 'react-modal';
import MenuIcon from 'assets/svgs/menu.svg';
import XIcon from 'assets/svgs/x.svg';
import { getQuery } from 'utils/query.utils';

const StyledFlexContainer = styled(FlexContainer)`
  height: 80px;
  border-bottom: 1px #1f04121a solid;
  padding: 0 25px;
`;

const FixedContainer = styled(FlexContainer)`
  max-width: 1170px;
`;

const LogoWrapper = styled(FlexContainer)`
  flex: 0 1;
  cursor: pointer;
  > a {
    > img {
      display: inline-block;
      height: auto;
      width: 100px;
    }
  }

  @media only screen and (max-width: 769px) {
    display: inline-block;
  }
`;

const LinkStyled = styled(P)`
  text-decoration: none;
  margin-left: 15px;
  margin-right: 15px;
  cursor: pointer;
`;

const MobileLinkStyled = styled(P)<{ active: boolean }>`
  margin: 16px 0;
  font-size: 20px;
  font-weight: bold;
  color: ${({ active }) => (active ? '#09bba6' : 'initial')};
`;

const MenuItemsContainer = styled(FlexContainer)`
  margin-left: 130px;
  @media only screen and (max-width: 769px) {
    display: none;
  }
`;

const ButtonsContainer = styled(FlexContainer)`
  @media only screen and (max-width: 769px) {
    display: none;
  }
`;

const MobileMenuIcon = styled(FlexContainer)`
  display: none;
  @media only screen and (max-width: 769px) {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 40px;
    width: 40px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.color.bgLight};
    cursor: pointer;
    user-select: none;
  }
`;

ReactModal.setAppElement('#___gatsby');

interface NavButton {
  name: string;
  location?: string;
  inverted?: boolean;
}

interface OwnProps {
  nav?: boolean;
  buttons?: NavButton[];
  links?: Array<{ name: string; location: string }>;
}

const defaultButtons: NavButton[] = [
  {
    name: 'SignIn',
    location: APP_URL,
    inverted: true,
  },
  {
    name: 'Try for Free',
    location: APP_URL,
    inverted: false,
  },
];

const Header: React.FC<OwnProps> = ({
  nav = true,
  buttons = defaultButtons,
  links = MENU_ITEMS,
}) => {
  const [modalIsOpen, setIsOpen] = React.useState(false);

  function openModal() {
    setIsOpen((prevState) => !prevState);
  }

  function closeModal() {
    setIsOpen(false);
  }

  const { pathname, search } = useLocation();
  const isEditor = getQuery(search, 'editor') || pathname.includes('editor');

  return (
    <StyledFlexContainer justifyContent="center">
      <FixedContainer
        flexGrow
        justifyContent="space-between"
        alignItems="center"
      >
        <LogoWrapper>
          <Link to={isEditor ? '/editor' : '/'}>
            <img src={MintaLogo} alt="Minta Logo" />
          </Link>
        </LogoWrapper>
        {nav && (
          <>
            <MenuItemsContainer>
              {links.map(({ name, location }) => (
                <LinkStyled
                  key={name}
                  light
                  small
                  onClick={() => {
                    navigate(location);
                  }}
                >
                  {name}
                </LinkStyled>
              ))}
            </MenuItemsContainer>
            <ButtonsContainer>
              {buttons && (
                <>
                  {buttons.map(({ name, location, inverted }) => (
                    <Button
                      small
                      inverted={inverted}
                      href={location || APP_URL}
                      data-event_category="AppCta"
                      data-event_action={name}
                      data-event_label="button"
                      className="app_cta"
                    >
                      {name}
                    </Button>
                  ))}
                </>
              )}
            </ButtonsContainer>
            <ReactModal
              closeTimeoutMS={200}
              shouldFocusAfterRender={false}
              className="ReactModal__Content-menu"
              onRequestClose={closeModal}
              overlayClassName="overlay-menu"
              isOpen={modalIsOpen}
              bodyOpenClassName="on-open-menu"
            >
              <MobileLinkStyled
                key="home"
                onClick={() => {
                  navigate('/');
                }}
                active={pathname === '/'}
              >
                Home
              </MobileLinkStyled>
              {links.map(({ name, location }) => (
                <MobileLinkStyled
                  key={name}
                  onClick={() => {
                    navigate(location);
                  }}
                  active={pathname.includes(location)}
                >
                  {name}
                </MobileLinkStyled>
              ))}
            </ReactModal>
            <MobileMenuIcon onClick={openModal}>
              {modalIsOpen ? <XIcon /> : <MenuIcon />}
            </MobileMenuIcon>
          </>
        )}
      </FixedContainer>
    </StyledFlexContainer>
  );
};

export default Header;
